import { Api } from '@/services/Api';
import {
  CmpExportFile,
  CmpImportHierarchy,
  CmpImportMediaResultItem,
  CmpImportResponse,
  CmpImportResultItem,
  CmpImportSummary,
} from '@/declarations/models/ContentMigrationImport';

/**
 * Import an export file as a sequence of chunks, reporting progress as it goes.
 *
 * **Why chunk at all.** The import is one request that builds every page server-side,
 * so a 472-item file left the operator watching a spinner for minutes with no way to
 * tell a slow import from a crashed one. Each chunk is a complete envelope carrying a
 * slice of `items`, so the same endpoint handles it unchanged and M24 commits per
 * request - which is what makes partial progress both visible and real.
 *
 * **Chunks go out strictly in sequence, never in parallel.** The platform orders items
 * parents-before-children, because M24 attaches a child to whichever parent already
 * exists and never creates one on an item's behalf - so a child that arrives before
 * its parent lands at the site root and stays there. Sequential requests preserve that
 * order; concurrent ones would not.
 *
 * **A failure stops the run rather than carrying on.** Chunks already sent are
 * committed and their pages exist, which is reported back rather than hidden - and
 * re-importing the same file is safe, because M24 leaves pages it already has alone,
 * so the remedy is simply to run it again.
 *
 * **The media library goes first, in its own requests.** The envelope's `media` list
 * is every file the source has in EMS - several hundred of them - and it belongs to no
 * single item, so it is sent before the items and is left *out* of every item chunk.
 * Leaving it in would re-send the whole library twenty times over and have M24 upsert
 * every row again per chunk. Sending it first also means the gallery is complete even
 * if a later item chunk fails.
 */

/** Matches `jobs.museum24_push.DEFAULT_BATCH_SIZE` on the platform: a representative
 *  item's payload is ~13 KB, so 25 keeps a request a few hundred KB while keeping the
 *  number of round trips - and the progress steps - sensible. */
export const IMPORT_CHUNK_SIZE = 25;

/** Larger than the item chunk because a media entry is a few hundred bytes against an
 *  item's ~13 KB, and M24 does one upsert per entry rather than building a page. */
export const MEDIA_CHUNK_SIZE = 200;

export interface ImportPhaseProgress {
  /** Handed over so far, across completed chunks. */
  done: number;
  total: number;
}

export interface ImportProgress {
  /** The media library, sent first. */
  media: ImportPhaseProgress;
  items: ImportPhaseProgress;
}

export interface ChunkedImportResult {
  summary: CmpImportSummary;
  /** Totals for the site's media library. */
  mediaSummary: CmpImportSummary;
  /** Every file M24 answered for - the per-file record of what the gallery gained. */
  mediaResults: Array<CmpImportMediaResultItem>;
  /** Every item M24 answered for, in the order the chunks came back - the per-page
   * record of what the run did, which the aggregate counts alone cannot give. Items in
   * a chunk that never ran are absent, which is what `failure` explains. */
  results: Array<CmpImportResultItem>;
  /** Set when a chunk failed: everything before it is committed, nothing after ran. */
  failure?: { message: string; itemsNotAttempted: number };
}

const EMPTY: CmpImportSummary = { stored: 0, unchanged: 0, skipped: 0, rejected: 0, total: 0 };

/** The two summaries added together, key by key. */
function plus(left: CmpImportSummary, right?: CmpImportSummary): CmpImportSummary {
  if (!right) return left;
  const total = { ...left };
  (Object.keys(EMPTY) as Array<keyof CmpImportSummary>).forEach((key) => {
    total[key] = left[key] + (right[key] ?? 0);
  });
  return total;
}

export async function importCmpExportInChunks(
  siteId: number,
  file: CmpExportFile,
  filename: string,
  onProgress: (progress: ImportProgress) => void,
  chunkSize: number = IMPORT_CHUNK_SIZE,
  mediaChunkSize: number = MEDIA_CHUNK_SIZE,
  hierarchy: CmpImportHierarchy = 'keep',
): Promise<ChunkedImportResult> {
  const items = file.items ?? [];
  const media = file.media ?? [];
  const total = items.length;
  let summary: CmpImportSummary = { ...EMPTY };
  let mediaSummary: CmpImportSummary = { ...EMPTY };
  const results: Array<CmpImportResultItem> = [];
  const mediaResults: Array<CmpImportMediaResultItem> = [];
  const progress: ImportProgress = {
    media: { done: 0, total: media.length },
    items: { done: 0, total },
  };

  const report = () => onProgress({ media: { ...progress.media }, items: { ...progress.items } });

  report();

  /** One request: either what M24 answered, or why it could not be sent. */
  const send = async (envelope: object): Promise<{ data?: CmpImportResponse; error?: string }> => {
    const blob = new Blob([JSON.stringify(envelope)], { type: 'application/json' });
    // The same choice on every chunk, media and items alike: the structure decision
    // belongs to the run, and M24 reads it per request.
    const [data, error] = await Api.importContentMigrationFile(siteId, blob, filename, hierarchy).fetch();
    if (error || !data) return { error: error?.message ?? 'Importen feilet.' };
    return { data };
  };

  for (let offset = 0; offset < media.length; offset += mediaChunkSize) {
    const slice = media.slice(offset, offset + mediaChunkSize);
    // No items: a library import is a complete, valid request on its own, which is
    // what keeps the two phases independent.
    // eslint-disable-next-line no-await-in-loop
    const sent = await send({ ...file, items: [], media: slice });
    if (!sent.data) {
      return {
        summary,
        results,
        mediaSummary,
        mediaResults,
        failure: { message: sent.error ?? 'Importen feilet.', itemsNotAttempted: total },
      };
    }
    mediaSummary = plus(mediaSummary, sent.data.media_summary);
    sent.data.media_results?.forEach((result) => mediaResults.push(result));
    progress.media.done = Math.min(offset + slice.length, media.length);
    report();
  }

  for (let offset = 0; offset < total; offset += chunkSize) {
    const slice = items.slice(offset, offset + chunkSize);
    // A complete envelope per chunk, so the endpoint needs no notion of chunking and
    // every request is independently valid. `export` metadata is carried along
    // unchanged; the receiver ignores it. `media` is emptied because the library was
    // sent above - repeating it per chunk would re-upsert every row twenty times.
    // Sequential on purpose - see this module's docstring: the platform orders items
    // parents-before-children and Museum24 attaches a child only to a parent that
    // already exists, so concurrent chunks would strand children at the site root.
    // eslint-disable-next-line no-await-in-loop
    const sent = await send({ ...file, items: slice, media: [] });
    if (!sent.data) {
      return {
        summary,
        results,
        mediaSummary,
        mediaResults,
        failure: { message: sent.error ?? 'Importen feilet.', itemsNotAttempted: total - offset },
      };
    }
    summary = plus(summary, sent.data.summary);
    sent.data.results?.forEach((result) => results.push(result));
    progress.items.done = Math.min(offset + slice.length, total);
    report();
  }

  return { summary, results, mediaSummary, mediaResults };
}
